// @flow
import React from 'react';
import injectSheet from 'react-jss';
import { DOMParser } from '@xmldom/xmldom';
import SocialIcon from 'shared/components/SocialIcon';
import classNames from 'classnames';
import useLazyLoad from 'shared/hooks/useLazyLoad';
import CleanedHtml from 'shared/components/CleanedHtml';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

type Icon = {
  url: string,
  icon: string
}

type ColumnContent = {
  enable_icons: Boolean,
  header: string,
  icons: Array<Icon>,
  text: string,
}

type Props = {
  classes: Object,
  scrollAnimation: Boolean,
  left_col: ColumnContent,
  enable_right_col: Boolean,
  right_col: ColumnContent,
};

const isEmptyHeader = (header) => {
  if(!header) return true;
  const parser = new DOMParser();
  const dom = parser.parseFromString(header, 'text/xml');
  if(!dom.documentElement) return false;
  return !dom.documentElement.textContent;
}

const ContactBlock = (props: Props) => {
  const {
    classes: c, left_col, right_col, scrollAnimation, enable_right_col: enableRightCol
  } = props;
  const { ref, isIntersectingElement } = useLazyLoad();

  const animationPlayed = scrollAnimation && isIntersectingElement;

  const iconStyle = {
    fontSize: '28px',
    color: 'black',
    marginLeft: '8px',
    marginRight: '8px',
    textDecoration: 'none',
    cursor: 'pointer'
  };
  const headerContent = (header, css) => {
    if(isEmptyHeader(header)){
      header = '&nbsp;'
    }
    return (<CleanedHtml className={css}>{header}</CleanedHtml>);
  }


  const textContent = (text, css) => (
    <CleanedHtml className={css}>{text}</CleanedHtml>
  );
  const iconContent = icons => (
    <div
      className={classNames(c.iconContainer, animationPlayed && c.animated)}
      ref={ref}
    >
      {icons.map((link, i) => (
        <SocialIcon
          key={i}
          iconStyle={iconStyle}
          link={link}
        />
      ))}
    </div>
  );

  return (
    <div
      className={c.container}
    >
      <div className={classNames(c.column, c.left_column)} >
        {headerContent(left_col.header, classNames(c.header, c.left_header))}
        {left_col.text !== '<p><br></p>' && textContent(left_col.text, classNames(c.text, c.left_text))}
        {left_col.enable_icons && iconContent(left_col.icons)}
      </div>
      {enableRightCol && (
      <div className={classNames(c.column, c.right_column)}>
        {headerContent(right_col.header, classNames(c.header, c.right_header))}
        {right_col.text !== '<p><br></p>' && textContent(right_col.text, classNames(c.text, c.right_text))}
        {right_col.enable_icons && iconContent(right_col.icons)}
      </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '54px',
    '@media(max-width: 960px)': {
      flexWrap: ({ right_col: { desktop, tablet, mobile } }) =>
        desktop?.show_in_new_line ||
        tablet?.show_in_new_line ||
        mobile?.show_in_new_line
          ? null
          : 'wrap',
    },
    '@media(max-width: 576px)': {
      flexWrap: ({ right_col: { mobile } }) =>
        mobile?.show_in_new_line ? 'wrap' : null,
    },
    '@media(max-width: 768px) and (min-width:577px)': {
      flexWrap: ({ right_col: { tablet } }) =>
        tablet?.show_in_new_line ? 'wrap' : null,
    },
    '@media(min-width: 769px)': {
      flexWrap: ({ right_col: { desktop } }) =>
        desktop?.show_in_new_line ? 'wrap' : null,
    },
  },
  column: {
    width: (props) => {
      if (props.enable_right_col) {
        return '50%';
      }
      return '100%';
    },
    display: 'inline-block',
    marginRight: '36px',
    '@media(max-width: 960px)': {
      width: ({ right_col: { desktop, tablet, mobile } }) =>
        desktop?.show_in_new_line ||
        tablet?.show_in_new_line ||
        mobile?.show_in_new_line
          ? null
          : '100%',
      display: ({ right_col: { desktop, tablet, mobile } }) =>
        desktop?.show_in_new_line ||
        tablet?.show_in_new_line ||
        mobile?.show_in_new_line
          ? null
          : 'block',
    },
    '@media(max-width: 576px)': {
      width: ({ right_col: { mobile } }) =>
        mobile?.show_in_new_line ? '100%' : null,
      display: ({ right_col: { mobile } }) =>
        mobile?.show_in_new_line ? 'block' : null,
    },
    '@media(max-width: 768px) and (min-width:577px)': {
      width: ({ right_col: { tablet } }) =>
        tablet?.show_in_new_line ? '100%' : null,
      display: ({ right_col: { tablet } }) =>
        tablet?.show_in_new_line ? 'block' : null,
    },
    '@media(min-width: 769px)': {
      width: ({ right_col: { desktop } }) =>
        desktop?.show_in_new_line ? '100%' : null,
      display: ({ right_col: { desktop } }) =>
        desktop?.show_in_new_line ? 'block' : null,
    },
  },
  left_column: {
    '@media(max-width: 960px)': {
      marginTop: ({left_col: {mobile, tablet, desktop}}) => mobile?.margin_top || tablet?.margin_top || desktop?.margin_top ? null : '36px',
      marginRight: ({left_col: {mobile, tablet, desktop}}) => mobile?.margin_right || tablet?.margin_right || desktop?.margin_right ? null : '0px'
    },
    '@media(max-width: 576px)': {
      marginTop: ({left_col: {mobile}}) => mobile?.margin_top || null,
      marginLeft: ({left_col: {mobile}}) => mobile?.margin_left || null,
      marginRight: ({left_col: {mobile}}) => mobile?.margin_right || null,
    },
    '@media(max-width: 768px) and (min-width: 577px)': {
      marginTop: ({left_col: {tablet}}) => tablet?.margin_top || null,
      marginLeft: ({left_col: {tablet}}) => tablet?.margin_left || null,
      marginRight: ({left_col: {tablet}}) => tablet?.margin_right || null,
    },
    '@media(min-width: 769px)': {
      marginTop: ({left_col: {desktop}}) => desktop?.margin_top || null,
      marginLeft: ({left_col: {desktop}}) => desktop?.margin_left || null,
      marginRight: ({left_col: {desktop}}) => desktop?.margin_right || null,
    },
  },
  right_column: {
    '@media(max-width: 960px)': {
      marginTop: ({right_col: {mobile, tablet, desktop}}) => mobile?.margin_top || tablet?.margin_top || desktop?.margin_top ? null : '36px',
      marginRight: ({right_col: {mobile, tablet, desktop}}) => mobile?.margin_right || tablet?.margin_right || desktop?.margin_right ? null : '0px'
    },
    '@media(max-width: 576px)': {
      marginTop: ({right_col: {mobile}}) => mobile?.margin_top || null,
      marginLeft: ({right_col: {mobile}}) => mobile?.margin_left || null,
      marginRight: ({right_col: {mobile}}) => mobile?.margin_right || null,
    },
    '@media(max-width: 768px) and (min-width: 577px)': {
      marginTop: ({right_col: {tablet}}) => tablet?.margin_top || null,
      marginLeft: ({right_col: {tablet}}) => tablet?.margin_left || null,
      marginRight: ({right_col: {tablet}}) => tablet?.margin_right || null,
    },
    '@media(min-width: 769px)': {
      marginTop: ({right_col: {desktop}}) => desktop?.margin_top || null,
      marginLeft: ({right_col: {desktop}}) => desktop?.margin_left || null,
      marginRight: ({right_col: {desktop}}) => desktop?.margin_right || null,
    },
  },
  header: {
    borderBottom: '1px solid rgb(234, 234, 234)',
    fontSize: '15px',
    fontWeight: '600',
    letterSpacing: '.1em',
    fontFamily: FONT_FAMILY_ROBOTO,
    paddingBottom: '36px',
    textTransform: 'uppercase',
  },
  left_header: {
    paddingBottom: ({left_col:{header_padding_bottom}}) => header_padding_bottom,
  },
  right_header: {
    paddingBottom: ({right_col:{header_padding_bottom}}) => header_padding_bottom,
    '@media(max-width: 576px)': {
      display: ({ right_col: { header, mobile } }) => isEmptyHeader(header) && !mobile?.show_empty_header ? 'none': null,
    },
    '@media(max-width: 768px) and (min-width:577px)': {
      display: ({ right_col: { header, tablet } }) => isEmptyHeader(header) && !tablet?.show_empty_header ? 'none': null,
    },
    '@media(min-width: 769px)': {
      display: ({ right_col: { header, desktop } }) => isEmptyHeader(header) && !desktop?.show_empty_header ? 'none': null,
    },
  },
  text: {
    fontSize: '17px',
    fontWeight: '300',
    lineHeight: ({left_col: {text_line_height}}) => text_line_height || '2em',
    fontFamily: FONT_FAMILY_ROBOTO,
    '& h1, & h2': {
      fontWeight: '300',
      lineHeight: ({left_col: {text_line_height}}) => text_line_height || '1.5',
      fontSize: '20px',
      marginBottom: '30px',
    },
    '& h3': {
      fontWeight: '600',
      lineHeight: ({left_col: {text_line_height}}) => text_line_height || '1.75',
      fontSize: '13px',
      letterSpacing: '.1em',
    },
    '& h4': {
      fontSize: '12px',
      fontWeight: '600',
      letterSpacing: '.1em',
      display: 'flex',
      alignItems: 'center',
      textTransform: 'uppercase',
      '& > a': {
        '&:after': {
          content: '"\\f178"',
          fontFamily: 'ff-icons',
          fontSize: '12px',
          marginLeft: '10px',
        },
      },
    },
    '& a': {
      color: 'black',
      textDecoration: 'underline',
      textDecorationStyle: 'dotted',
      cursor: 'pointer',
    },
    '& p, & h1, & h2, & h3, & h4': {
      marginTop: ({left_col: {text_line_vertical_margin}}) => text_line_vertical_margin,
      marginBottom: ({left_col: {text_line_vertical_margin}}) => text_line_vertical_margin,
    }
  },
  left_text: {
    paddingTop: ({left_col: {text_padding_top}}) => text_padding_top,
  },
  right_text: {
    paddingTop: ({right_col: {text_padding_top}}) => text_padding_top,
  },
  iconContainer: {
    display: 'flex',
    marginTop: '36px',
    opacity: (props) => {
      if (props.scrollAnimation) {
        return '0';
      }
      return '1';
    },
    transform: (props) => {
      if (props.scrollAnimation) {
        return 'translateY(100%)';
      }
      return 'none';
    },
    transition: '.6s ease-in-out',
  },
  animated: {
    opacity: '1 !important',
    transform: 'none !important',
  },
};

export default injectSheet(styles)(ContactBlock);
